import React, { useState, useEffect } from "react";
import { DefaultPageLayout } from "../ui/layouts/DefaultPageLayout";
import { Avatar } from "../ui/components/Avatar";
import { Badge } from "../ui/components/Badge";
import { Button } from "../ui/components/Button";
import { DropdownMenu } from "../ui/components/DropdownMenu";
import { IconWithBackground } from "../ui/components/IconWithBackground";
import { TextField } from "../ui/components/TextField";
import * as SubframeCore from "@subframe/core";
import {
  FeatherMessageSquare,
  FeatherFileText,
  FeatherUsers,
  FeatherSearch,
  FeatherChevronDown,
  FeatherCircle,
  FeatherClock,
  FeatherAlertCircle,
  FeatherCalendar,
  FeatherMessageCircle,
} from "@subframe/core";
import { useAuth } from "../features/auth";
import { useQueries } from "../shared/queries/presentation/hooks/useQueries";
import { queriesApi } from "../shared/queries/data/api/queriesApi";

interface Task {
  id: number;
  type: "query" | "transaction" | "vendor_review";
  title: string;
  description: string;
  userName: string;
  userImage?: string;
  userInitials: string;
  timeAgo: string;
  status: "response_required" | "awaiting_client_response" | "pending_review";
  period: string;
  responseValue?: string;
}

interface TaskCounts {
  queriesPending: number;
}

const TaskOverviewPage: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [timePeriodFilter, setTimePeriodFilter] = useState<string>("all");
  const [responseValues, setResponseValues] = useState<Record<number, string>>({});
  const [taskCounts, setTaskCounts] = useState<TaskCounts>({
    queriesPending: 0,
  });
  const [tasks, setTasks] = useState<Task[]>([]);

  const userId = user?.id ? user.id : undefined;
  
  // Map UI status filter to query status
  const getQueryStatus = () => {
    if (statusFilter === "all") return undefined;
    // For now, all our status filters map to "open" queries that need response
    if (["response_required", "awaiting_client_response", "pending_review"].includes(statusFilter)) {
      return "open";
    }
    return statusFilter;
  };

  const { assignedQueries, counts, loading: queriesLoading, refetch } = useQueries(
    Number(userId),
    {
      status: getQueryStatus(),
      sort_by: "created_at",
      sort_order: "desc",
    },
    "assigned"
  );

  useEffect(() => {
    fetchTaskCounts();
  }, [counts]);

  useEffect(() => {
    fetchTasks();
  }, [assignedQueries, taskTypeFilter, statusFilter, timePeriodFilter, searchTerm]);

  const fetchTaskCounts = async () => {
    try {
      if (counts) {
        setTaskCounts({
          queriesPending: counts.assignedToMe?.total || 0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch task counts:", error);
    }
  };

  const fetchTasks = () => {
    try {
      const allTasks: Task[] = [];

      // Map queries to tasks
      if (taskTypeFilter === "all" || taskTypeFilter === "query") {
        assignedQueries.forEach((query) => {
          const createdAt = new Date(query.createdAt);
          const now = new Date();
          const diffTime = Math.abs(now.getTime() - createdAt.getTime());
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          let timeAgo = "";
          if (diffDays < 7) {
            timeAgo = `${diffDays} days ago`;
          } else if (diffDays < 30) {
            const weeks = Math.floor(diffDays / 7);
            timeAgo = `${weeks} week${weeks > 1 ? "s" : ""} ago`;
          } else if (diffDays < 90) {
            const months = Math.floor(diffDays / 30);
            timeAgo = `${months} month${months > 1 ? "s" : ""} ago`;
          } else {
            timeAgo = `${Math.floor(diffDays / 30)} months ago`;
          }

          const description = query.description || "No description";
          allTasks.push({
            id: query.queryId,
            type: "query",
            title: description.substring(0, 50) + (description.length > 50 ? "..." : ""),
            description: description,
            userName: query.raisedBy.userName || query.raisedBy.email || "Unknown",
            userInitials: (query.raisedBy.userName || query.raisedBy.email || "U").substring(0, 2).toUpperCase(),
            timeAgo,
            status: query.status === "open" ? "response_required" : "awaiting_client_response",
            period: createdAt.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
          });
        });
      }

      // TODO: Add transactions and vendor reviews when APIs are available
      // For now, we'll use mock data structure

      // Filter by search term
      const filteredTasks = searchTerm
        ? allTasks.filter(
            (task) =>
              task.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
              task.userName.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : allTasks;

      setTasks(filteredTasks);
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
    }
  };

  const handleSendResponse = async (taskId: number, taskType: string) => {
    const responseText = responseValues[taskId];
    if (!responseText || !responseText.trim()) {
      return;
    }

    try {
      if (taskType === "query") {
        await queriesApi.addComment(taskId, {
          comment: responseText,
        });
        setResponseValues((prev) => ({ ...prev, [taskId]: "" }));
        refetch();
        fetchTasks();
      }
      // TODO: Handle other task types
    } catch (error) {
      console.error("Failed to send response:", error);
      alert("Failed to send response. Please try again.");
    }
  };

  const handleApproveTransaction = async (taskId: number) => {
    try {
      // TODO: Connect to transaction approval API
      console.log("Approving transaction:", taskId);
      alert("Transaction approval functionality will be connected to backend");
    } catch (error) {
      console.error("Failed to approve transaction:", error);
    }
  };

  const handleReviewVendor = async (taskId: number) => {
    try {
      // TODO: Connect to vendor review API
      console.log("Reviewing vendor:", taskId);
      alert("Vendor review functionality will be connected to backend");
    } catch (error) {
      console.error("Failed to review vendor:", error);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "response_required":
        return "brand";
      case "awaiting_client_response":
        return "warning";
      case "pending_review":
        return "warning";
      default:
        return "neutral";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "response_required":
        return "Response required";
      case "awaiting_client_response":
        return "Awaiting client response";
      case "pending_review":
        return "Pending Review";
      default:
        return status;
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex grow shrink-0 basis-0 flex-col items-start gap-8 px-8 py-8">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Task Overview
          </span>
        </div>
        <div className="flex w-full flex-col items-start gap-12">
          {/* Overview Cards */}
          <div className="flex w-full items-start gap-6">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
              <IconWithBackground
                variant="brand"
                size="small"
                icon={<FeatherMessageSquare />}
              />
              <div className="flex flex-col items-start gap-2">
                <span className="text-body font-body text-default-font">
                  {taskCounts.queriesPending} queries pending response
                </span>
              </div>
            </div>
          </div>

          {/* Tasks List */}
          <div className="flex w-full items-start gap-6">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    All Tasks
                  </span>
                  <Badge variant="neutral">{tasks.length}</Badge>
                </div>
                <div className="flex items-center gap-2 cursor-pointer" />
              </div>
              <div className="flex w-full items-center justify-between">
                <TextField
                  variant="filled"
                  label=""
                  helpText=""
                  icon={<FeatherSearch />}
                >
                  <TextField.Input
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchTerm(event.target.value)
                    }
                  />
                </TextField>
                <SubframeCore.DropdownMenu.Root>
                  <SubframeCore.DropdownMenu.Trigger asChild={true}>
                    <Button
                      variant="neutral-secondary"
                      iconRight={<FeatherChevronDown />}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                    >
                      {taskTypeFilter === "all" ? "Task Type" : taskTypeFilter === "query" ? "Queries" : taskTypeFilter === "transaction" ? "Transaction Requests" : "Vendor Review"}
                    </Button>
                  </SubframeCore.DropdownMenu.Trigger>
                  <SubframeCore.DropdownMenu.Portal>
                    <SubframeCore.DropdownMenu.Content
                      side="bottom"
                      align="start"
                      sideOffset={4}
                      asChild={true}
                    >
                      <DropdownMenu>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherMessageSquare />}
                          onClick={() => setTaskTypeFilter("all")}
                        >
                          All Types
                        </DropdownMenu.DropdownItem>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherMessageSquare />}
                          onClick={() => setTaskTypeFilter("query")}
                        >
                          Queries
                        </DropdownMenu.DropdownItem>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherFileText />}
                          onClick={() => setTaskTypeFilter("transaction")}
                        >
                          Transaction Requests
                        </DropdownMenu.DropdownItem>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherUsers />}
                          onClick={() => setTaskTypeFilter("vendor_review")}
                        >
                          Vendor Review
                        </DropdownMenu.DropdownItem>
                      </DropdownMenu>
                    </SubframeCore.DropdownMenu.Content>
                  </SubframeCore.DropdownMenu.Portal>
                </SubframeCore.DropdownMenu.Root>
                <SubframeCore.DropdownMenu.Root>
                  <SubframeCore.DropdownMenu.Trigger asChild={true}>
                    <Button
                      variant="neutral-secondary"
                      iconRight={<FeatherChevronDown />}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                    >
                      {statusFilter === "all" ? "Status" : getStatusLabel(statusFilter)}
                    </Button>
                  </SubframeCore.DropdownMenu.Trigger>
                  <SubframeCore.DropdownMenu.Portal>
                    <SubframeCore.DropdownMenu.Content
                      side="bottom"
                      align="start"
                      sideOffset={4}
                      asChild={true}
                    >
                      <DropdownMenu>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherCircle />}
                          onClick={() => setStatusFilter("all")}
                        >
                          All Statuses
                        </DropdownMenu.DropdownItem>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherCircle />}
                          onClick={() => setStatusFilter("response_required")}
                        >
                          Response Required
                        </DropdownMenu.DropdownItem>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherClock />}
                          onClick={() => setStatusFilter("awaiting_client_response")}
                        >
                          Awaiting Client Response
                        </DropdownMenu.DropdownItem>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherAlertCircle />}
                          onClick={() => setStatusFilter("pending_review")}
                        >
                          Pending Review
                        </DropdownMenu.DropdownItem>
                      </DropdownMenu>
                    </SubframeCore.DropdownMenu.Content>
                  </SubframeCore.DropdownMenu.Portal>
                </SubframeCore.DropdownMenu.Root>
                <SubframeCore.DropdownMenu.Root>
                  <SubframeCore.DropdownMenu.Trigger asChild={true}>
                    <Button
                      variant="neutral-secondary"
                      iconRight={<FeatherChevronDown />}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                    >
                      Time Period
                    </Button>
                  </SubframeCore.DropdownMenu.Trigger>
                  <SubframeCore.DropdownMenu.Portal>
                    <SubframeCore.DropdownMenu.Content
                      side="bottom"
                      align="start"
                      sideOffset={4}
                      asChild={true}
                    >
                      <DropdownMenu>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherCalendar />}
                          onClick={() => setTimePeriodFilter("7days")}
                        >
                          Last 7 days
                        </DropdownMenu.DropdownItem>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherCalendar />}
                          onClick={() => setTimePeriodFilter("30days")}
                        >
                          Last 30 days
                        </DropdownMenu.DropdownItem>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherCalendar />}
                          onClick={() => setTimePeriodFilter("3months")}
                        >
                          Last 3 months
                        </DropdownMenu.DropdownItem>
                        <DropdownMenu.DropdownItem
                          icon={<FeatherCalendar />}
                          onClick={() => setTimePeriodFilter("all")}
                        >
                          All time
                        </DropdownMenu.DropdownItem>
                      </DropdownMenu>
                    </SubframeCore.DropdownMenu.Content>
                  </SubframeCore.DropdownMenu.Portal>
                </SubframeCore.DropdownMenu.Root>
              </div>

              {/* Task Cards */}
              {queriesLoading ? (
                <div className="flex w-full items-center justify-center py-8">
                  <span className="text-body text-neutral-500">Loading tasks...</span>
                </div>
              ) : tasks.length === 0 ? (
                <div className="flex w-full items-center justify-center py-8">
                  <span className="text-body text-neutral-500">No tasks found</span>
                </div>
              ) : (
                tasks.map((task) => (
                  <div
                    key={task.id}
                    className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm"
                  >
                    <div className="flex w-full items-start gap-4">
                      <Avatar size="medium" image={task.userImage}>
                        {task.userInitials}
                      </Avatar>
                      <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
                        <div className="flex w-full items-center gap-2">
                          <div className="flex grow shrink-0 basis-0 flex-col items-start justify-between">
                            <span className="text-body-bold font-body-bold text-default-font">
                              {task.userName}
                            </span>
                            <span className="text-caption font-caption text-subtext-color">
                              {task.timeAgo}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                task.type === "query"
                                  ? "brand"
                                  : task.type === "transaction"
                                  ? "warning"
                                  : "success"
                              }
                              icon={
                                task.type === "query" ? (
                                  <FeatherMessageSquare />
                                ) : task.type === "transaction" ? (
                                  <FeatherFileText />
                                ) : (
                                  <FeatherUsers />
                                )
                              }
                            >
                              {task.type === "query"
                                ? "Query"
                                : task.type === "transaction"
                                ? "Transaction"
                                : "Vendor Review"}
                            </Badge>
                            <Badge variant={getStatusBadgeVariant(task.status)}>
                              {getStatusLabel(task.status)}
                            </Badge>
                            <Badge variant="neutral">{task.period}</Badge>
                          </div>
                        </div>
                        <span className="text-body font-body text-default-font">
                          {task.description}
                        </span>
                        {task.type === "query" ? (
                          <div className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-2 mt-2">
                            <TextField
                              variant="filled"
                              label=""
                              helpText=""
                              className="flex-1"
                            >
                              <TextField.Input
                                placeholder="Add a response…"
                                value={responseValues[task.id] || ""}
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  setResponseValues((prev) => ({
                                    ...prev,
                                    [task.id]: event.target.value,
                                  }));
                                }}
                              />
                            </TextField>
                            <Button
                              variant="brand-primary"
                              size="small"
                              onClick={() => handleSendResponse(task.id, task.type)}
                              className="ml-2"
                            >
                              Send
                            </Button>
                          </div>
                        ) : task.type === "transaction" ? (
                          <div className="flex items-center gap-2 mt-2">
                            <Button
                              variant="brand-primary"
                              size="small"
                              onClick={() => handleApproveTransaction(task.id)}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="neutral-secondary"
                              size="small"
                              onClick={() => {}}
                            >
                              Review Details
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 mt-2">
                            <Button
                              variant="brand-primary"
                              size="small"
                              onClick={() => handleReviewVendor(task.id)}
                            >
                              Review Vendor
                            </Button>
                            <Button
                              variant="neutral-secondary"
                              size="small"
                              onClick={() => {}}
                            >
                              View Application
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Team Sidebar */}
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6 pt-9">
              <div className="flex w-full flex-col items-start gap-8 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-sm">
                <div className="flex w-full flex-col items-start gap-4">
                  <div className="flex w-full items-center justify-between">
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Your Zournal team
                    </span>
                    <IconWithBackground
                      variant="brand"
                      size="x-small"
                      icon={<FeatherMessageSquare />}
                      square={false}
                    />
                  </div>
                  <span className="text-body font-body text-subtext-color">
                    Have questions? Reach out to your dedicated team at Zournal.
                  </span>
                </div>
                <div className="flex w-full flex-col items-start gap-6">
                  <div className="flex w-full items-start gap-3">
                    <Avatar
                      variant="brand"
                      size="medium"
                      image="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400"
                      square={false}
                    >
                      AN
                    </Avatar>
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Allie Nolan
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        CFO
                      </span>
                    </div>
                    <Button
                      className="mt-1"
                      variant="brand-secondary"
                      size="small"
                      icon={<FeatherMessageCircle />}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                    >
                      Contact
                    </Button>
                  </div>
                  <div className="flex w-full items-start gap-3">
                    <Avatar
                      variant="success"
                      size="medium"
                      image="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400"
                      square={false}
                    >
                      JC
                    </Avatar>
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Jessica Chen
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        Bookkeeping
                      </span>
                    </div>
                    <Button
                      className="mt-1"
                      variant="brand-secondary"
                      size="small"
                      icon={<FeatherMessageCircle />}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                    >
                      Contact
                    </Button>
                  </div>
                  <div className="flex w-full items-start gap-3">
                    <Avatar variant="warning" size="medium" image="" square={false}>
                      PT
                    </Avatar>
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Pilot Tax Team
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        Tax
                      </span>
                    </div>
                    <Button
                      className="mt-1"
                      variant="brand-secondary"
                      size="small"
                      icon={<FeatherMessageCircle />}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                    >
                      Contact
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};

export default TaskOverviewPage;

